// src/tour/components/AppTourController.tsx
import { useEffect as useEffect4 } from "react";

// #style-inject:#style-inject
function styleInject(css, { insertAt } = {}) {
  if (!css || typeof document === "undefined") return;
  const head = document.head || document.getElementsByTagName("head")[0];
  const style = document.createElement("style");
  style.type = "text/css";
  if (insertAt === "top") {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }
  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

// src/tour/components/AppTourController.css
styleInject("body.app-tour-active .react-joyride__loader {\n  display: none !important;\n}\nbody.app-tour-active .react-joyride__tooltip:not([data-app-tour-tooltip]) {\n  display: none !important;\n}\nbody.app-tour-active [data-help-launcher=open],\nbody.app-tour-active [data-help-launcher=closed] {\n  opacity: 0;\n  pointer-events: none;\n}\n");

// src/tour/lib/tourRouting.ts
var isOnRoute = (step, currentPath) => {
  if (step.matchPaths?.length) {
    return step.matchPaths.includes(currentPath);
  }
  const routeMatch = step.routeMatch ?? "exact";
  if (routeMatch === "prefix") {
    return currentPath.startsWith(step.route);
  }
  return step.route === currentPath;
};
var getNavigationRoute = (step) => step.navigationRoute ?? step.route;
var hasReachedAdvancePath = (currentPath, advanceOnPath, match = "exact") => {
  if (match === "prefix") {
    return currentPath.startsWith(advanceOnPath);
  }
  return currentPath === advanceOnPath;
};
var canAdvanceInteractiveStep = (currentPath, previousPath, step, options) => {
  if (step.interactionMode !== "interactive" || !step.advanceOnPath) {
    return false;
  }
  const reached = hasReachedAdvancePath(
    currentPath,
    step.advanceOnPath,
    step.advanceOnPathMatch
  );
  if (!reached) {
    return false;
  }
  if (options?.forceIfReached) {
    return true;
  }
  const wasReached = hasReachedAdvancePath(
    previousPath,
    step.advanceOnPath,
    step.advanceOnPathMatch
  );
  if (!wasReached) {
    return true;
  }
  return !isOnRoute(step, currentPath);
};
var didTourStepBack = (previousStepIndex, activeStepIndex) => previousStepIndex !== null && activeStepIndex < previousStepIndex;
var shouldEndTourOnExternalNavigation = ({
  activeStep,
  currentPath,
  pendingTourNavigation,
  previousActiveStep,
  previousPath,
  step
}) => {
  if (pendingTourNavigation) {
    return false;
  }
  if (previousPath === currentPath) {
    return false;
  }
  if (didTourStepBack(previousActiveStep, activeStep)) {
    return false;
  }
  return !isOnRoute(step, currentPath);
};
var shouldAdvanceToNextStepRoute = (currentPath, step, nextStep) => {
  if (!nextStep || isOnRoute(step, currentPath)) {
    return false;
  }
  return isOnRoute(nextStep, currentPath);
};

// src/tour/components/AppTourJoyride.tsx
import { Joyride } from "react-joyride";
import { jsx } from "react/jsx-runtime";
var AppTourJoyride = ({
  activeStep,
  isRunning,
  joyrideSteps,
  onEvent,
  tourConfig
}) => /* @__PURE__ */ jsx(
  Joyride,
  {
    arrowComponent: tourConfig.arrowComponent,
    continuous: true,
    loaderComponent: null,
    locale: {
      back: "Back",
      close: "Close",
      last: "Done",
      next: "Next",
      nextWithProgress: "Next ({current} of {total})",
      open: "Open the dialog",
      skip: "Skip",
      ...tourConfig.locale
    },
    onEvent,
    options: {
      dismissKeyAction: tourConfig.allowKeyboardControl ? "close" : false,
      hideOverlay: true,
      showProgress: true,
      skipBeacon: true,
      spotlightPadding: 8,
      arrowSize: tourConfig.arrowSize ?? 8,
      arrowBase: tourConfig.arrowBase ?? 18,
      arrowSpacing: tourConfig.arrowSpacing ?? 6,
      offset: 8,
      width: 280,
      primaryColor: "#303030",
      textColor: "#303030",
      backgroundColor: "#ffffff",
      arrowColor: "#ffffff",
      zIndex: 1e4
    },
    portalElement: tourConfig.portalElement ?? void 0,
    run: isRunning,
    stepIndex: activeStep,
    steps: joyrideSteps,
    tooltipComponent: tourConfig.tooltipComponent
  }
);

// src/tour/hooks/useAdvanceOnClickListener.ts
import { useEffect } from "react";

// src/tour/lib/largeTargetPlacement.ts
var LARGE_TARGET_MIN_VIEWPORT_RATIO = 0.65;
var isTargetTooLargeForEdgePlacement = (rect, viewport = {
  height: window.innerHeight,
  width: window.innerWidth
}) => {
  const viewportHeight = viewport.height;
  const viewportWidth = viewport.width;
  if (viewportHeight <= 0 || viewportWidth <= 0) {
    return false;
  }
  const heightRatio = rect.height / viewportHeight;
  const widthRatio = rect.width / viewportWidth;
  return heightRatio >= LARGE_TARGET_MIN_VIEWPORT_RATIO || widthRatio >= LARGE_TARGET_MIN_VIEWPORT_RATIO;
};
var queryTourTargetElement = (selector) => {
  try {
    return document.querySelector(selector);
  } catch {
    return null;
  }
};

// src/tour/hooks/tourController.constants.ts
var INTERACTIVE_ADVANCE_POLL_MS = 100;
var INTERACTIVE_ADVANCE_POLL_TIMEOUT_MS = 5e3;
var DEFAULT_TOUR_CONFIG = {
  disableActiveInteraction: false,
  allowClose: true,
  allowKeyboardControl: true
};

// src/tour/hooks/useAdvanceOnClickListener.ts
var useAdvanceOnClickListener = ({
  activeStep,
  advance,
  isRunning,
  launchId,
  refs,
  steps
}) => {
  const { activeStepRef } = refs;
  const { advanceToNextStep } = advance;
  useEffect(() => {
    if (!isRunning || activeStep === null) {
      return;
    }
    const step = steps[activeStep];
    const advanceSelector = step?.advanceOnClick;
    if (!advanceSelector) {
      return;
    }
    let clickTarget = null;
    let pollId;
    const handleAdvanceClick = (event) => {
      if (!(event instanceof MouseEvent) || !event.isTrusted) {
        return;
      }
      if (activeStepRef.current !== activeStep) {
        return;
      }
      advanceToNextStep(activeStep);
    };
    const attachClickListener = () => {
      const container = queryTourTargetElement(advanceSelector);
      const button = container?.querySelector("button");
      clickTarget = button ?? container;
      if (!clickTarget) {
        return false;
      }
      clickTarget.addEventListener("click", handleAdvanceClick);
      return true;
    };
    if (!attachClickListener()) {
      pollId = window.setInterval(() => {
        if (activeStepRef.current !== activeStep) {
          return;
        }
        if (attachClickListener() && pollId !== void 0) {
          window.clearInterval(pollId);
          pollId = void 0;
        }
      }, INTERACTIVE_ADVANCE_POLL_MS);
    }
    return () => {
      if (pollId !== void 0) {
        window.clearInterval(pollId);
      }
      clickTarget?.removeEventListener("click", handleAdvanceClick);
    };
  }, [activeStep, activeStepRef, advanceToNextStep, isRunning, launchId, steps]);
};

// src/tour/hooks/useJoyrideStepsWithPlacement.ts
import { useCallback, useLayoutEffect, useMemo, useState } from "react";

// src/tour/lib/mapJoyridePlacement.ts
var mapJoyridePlacement = (side, align) => {
  if (!side) {
    return "auto";
  }
  if (!align || align === "center") {
    return side;
  }
  return `${side}-${align}`;
};

// src/tour/lib/toJoyrideSteps.ts
var DEFAULT_TARGET_WAIT_MS = 12e3;
var toJoyrideSteps = (steps, config) => steps.map((step, index) => {
  const isLastStep = index === steps.length - 1;
  const isInteractive = step.interactionMode === "interactive";
  const advanceOnClick = Boolean(step.advanceOnClick);
  const content = step.popover.description ?? "";
  const buttons = (() => {
    if (isInteractive || advanceOnClick) {
      const footer = ["skip", "back"];
      if (isLastStep) {
        footer.push("primary");
      }
      return footer;
    }
    if (config.allowClose) {
      return ["skip", "back", "close", "primary"];
    }
    return ["skip", "back", "primary"];
  })();
  const tooltipTarget = step.tooltipElement ?? step.element;
  const spotlightTarget = step.tooltipElement ? step.element : void 0;
  return {
    target: tooltipTarget,
    spotlightTarget,
    title: step.popover.title,
    content,
    placement: step.popover.placement === "center" ? "center" : mapJoyridePlacement(step.popover.side, step.popover.align),
    skipBeacon: true,
    skipScroll: true,
    floatingOptions: { hideArrow: true },
    blockTargetInteraction: !isInteractive && config.disableActiveInteraction,
    buttons,
    overlayClickAction: "close",
    targetWaitTimeout: step.waitTimeoutMs ?? DEFAULT_TARGET_WAIT_MS
  };
});

// src/tour/hooks/useJoyrideStepsWithPlacement.ts
var useJoyrideStepsWithPlacement = ({
  activeStep,
  coreTourConfig,
  currentPath,
  isRunning,
  launchId,
  refs,
  steps
}) => {
  const { activeStepRef, stepsRef } = refs;
  const baseJoyrideSteps = useMemo(
    () => toJoyrideSteps(steps, coreTourConfig),
    [coreTourConfig, steps]
  );
  const [largeTargetCenterStepIndex, setLargeTargetCenterStepIndex] = useState(null);
  const joyrideSteps = useMemo(() => {
    if (largeTargetCenterStepIndex === null) {
      return baseJoyrideSteps;
    }
    return baseJoyrideSteps.map(
      (joyrideStep, index) => index === largeTargetCenterStepIndex ? { ...joyrideStep, placement: "center" } : joyrideStep
    );
  }, [baseJoyrideSteps, largeTargetCenterStepIndex]);
  const updateLargeTargetPlacement = useCallback(
    (stepIndex) => {
      const appStep = stepsRef.current[stepIndex];
      const joyrideStep = baseJoyrideSteps[stepIndex];
      if (!appStep || !joyrideStep) {
        setLargeTargetCenterStepIndex(null);
        return;
      }
      if (appStep.popover.placement === "center" || appStep.tooltipElement) {
        setLargeTargetCenterStepIndex(null);
        return;
      }
      const spotlightEl = queryTourTargetElement(appStep.element);
      const tooltipEl = queryTourTargetElement(
        appStep.tooltipElement ?? String(joyrideStep.target)
      );
      const measureEl = spotlightEl ?? tooltipEl;
      if (measureEl && isTargetTooLargeForEdgePlacement(measureEl.getBoundingClientRect())) {
        setLargeTargetCenterStepIndex(stepIndex);
        return;
      }
      setLargeTargetCenterStepIndex(null);
    },
    [baseJoyrideSteps, stepsRef]
  );
  useLayoutEffect(() => {
    if (!isRunning || activeStep === null) {
      setLargeTargetCenterStepIndex(null);
      return;
    }
    updateLargeTargetPlacement(activeStep);
    const handleResize = () => {
      if (activeStepRef.current !== null) {
        updateLargeTargetPlacement(activeStepRef.current);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [activeStep, activeStepRef, currentPath, isRunning, launchId, updateLargeTargetPlacement]);
  return { joyrideSteps, updateLargeTargetPlacement };
};

// src/tour/hooks/useJoyrideTourEvents.ts
import { useCallback as useCallback2 } from "react";
import {
  ACTIONS,
  EVENTS,
  ORIGIN,
  STATUS
} from "react-joyride";
var useJoyrideTourEvents = ({
  advance,
  allowKeyboardControl,
  onStepTargetMissing,
  onTourEvent,
  refs,
  updateLargeTargetPlacement
}) => {
  const { activeStepRef, goToStepRef, stepsRef } = refs;
  const { endTour, startInteractiveAdvancePoll, tryAdvanceInteractiveStep } = advance;
  return useCallback2(
    (data) => {
      const { action, index, status, type, origin } = data;
      if (!allowKeyboardControl && action === ACTIONS.CLOSE && origin === ORIGIN.KEYBOARD) {
        return;
      }
      if (type === EVENTS.STEP_BEFORE && index === activeStepRef.current) {
        updateLargeTargetPlacement(index);
      }
      if (type === EVENTS.TARGET_NOT_FOUND) {
        const step = stepsRef.current[index];
        if (step?.interactionMode === "interactive") {
          if (tryAdvanceInteractiveStep(index, { forceIfReached: true })) {
            return;
          }
          startInteractiveAdvancePoll(index);
          return;
        }
        const selector = step?.element;
        if (selector) {
          onStepTargetMissing?.(index, selector);
          onTourEvent?.({ type: "target_not_found", stepIndex: index, selector });
        }
        endTour();
        return;
      }
      if (action === ACTIONS.SKIP) {
        onTourEvent?.({ type: "skip" });
        endTour();
        return;
      }
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        const step = stepsRef.current[index];
        if (step?.interactionMode === "interactive") {
          if (tryAdvanceInteractiveStep(index, { forceIfReached: true })) {
            return;
          }
          startInteractiveAdvancePoll(index);
          return;
        }
        onTourEvent?.({ type: "finished" });
        endTour();
        return;
      }
      if (type === EVENTS.STEP_AFTER || type === EVENTS.TOOLTIP) {
        if (action === ACTIONS.CLOSE) {
          onTourEvent?.({
            type: "close",
            source: origin === ORIGIN.KEYBOARD ? "keyboard" : "tooltip"
          });
          endTour();
          return;
        }
        if (action === ACTIONS.NEXT) {
          const nextStep = index + 1;
          if (nextStep >= stepsRef.current.length) {
            onTourEvent?.({ type: "finished" });
            endTour();
            return;
          }
          onTourEvent?.({ type: "step_advanced", stepIndex: index, advanceType: "next" });
          goToStepRef.current(nextStep);
          return;
        }
        if (action === ACTIONS.PREV && index > 0) {
          onTourEvent?.({ type: "step_advanced", stepIndex: index, advanceType: "back" });
          goToStepRef.current(index - 1);
        }
      }
    },
    [
      activeStepRef,
      allowKeyboardControl,
      endTour,
      goToStepRef,
      onStepTargetMissing,
      onTourEvent,
      startInteractiveAdvancePoll,
      stepsRef,
      tryAdvanceInteractiveStep,
      updateLargeTargetPlacement
    ]
  );
};

// src/tour/hooks/useNormalizedTourConfig.ts
import { useMemo as useMemo2 } from "react";
var useNormalizedTourConfig = (config) => {
  const tourConfig = useMemo2(
    () => ({ ...DEFAULT_TOUR_CONFIG, ...config }),
    [config]
  );
  const coreTourConfig = useMemo2(
    () => ({
      disableActiveInteraction: tourConfig.disableActiveInteraction ?? DEFAULT_TOUR_CONFIG.disableActiveInteraction,
      allowClose: tourConfig.allowClose ?? DEFAULT_TOUR_CONFIG.allowClose,
      allowKeyboardControl: tourConfig.allowKeyboardControl ?? DEFAULT_TOUR_CONFIG.allowKeyboardControl
    }),
    [tourConfig]
  );
  return { tourConfig, coreTourConfig };
};

// src/tour/hooks/useTourBodyClass.ts
import { useEffect as useEffect2 } from "react";
var useTourBodyClass = (isRunning) => {
  useEffect2(() => {
    document.body.classList.toggle("app-tour-active", isRunning);
    return () => {
      document.body.classList.remove("app-tour-active");
    };
  }, [isRunning]);
};

// src/tour/hooks/useTourControllerRefs.ts
import { useRef } from "react";
var useTourControllerRefs = ({
  activeStep,
  currentPath,
  goToStep,
  steps,
  stopTour
}) => {
  const stopTourRef = useRef(stopTour);
  const goToStepRef = useRef(goToStep);
  const stepsRef = useRef(steps);
  const previousPathRef = useRef(currentPath);
  const previousActiveStepRef = useRef(activeStep);
  const currentPathRef = useRef(currentPath);
  const activeStepRef = useRef(activeStep);
  const interactiveAdvancePollRef = useRef(void 0);
  const pendingTourNavigationRef = useRef(false);
  stopTourRef.current = stopTour;
  goToStepRef.current = goToStep;
  stepsRef.current = steps;
  currentPathRef.current = currentPath;
  activeStepRef.current = activeStep;
  return {
    activeStepRef,
    currentPathRef,
    goToStepRef,
    interactiveAdvancePollRef,
    pendingTourNavigationRef,
    previousActiveStepRef,
    previousPathRef,
    stepsRef,
    stopTourRef
  };
};

// src/tour/hooks/useTourRouteNavigation.ts
import { useEffect as useEffect3 } from "react";
var useTourRouteNavigation = ({
  activeStep,
  advance,
  currentPath,
  launchId,
  navigate,
  onTourEvent,
  refs,
  steps
}) => {
  const { pendingTourNavigationRef, previousActiveStepRef, previousPathRef } = refs;
  const { advanceToNextStep, endTour, tryAdvanceInteractiveStep } = advance;
  const navigateForTour = (step) => {
    pendingTourNavigationRef.current = true;
    navigate(getNavigationRoute(step));
  };
  useEffect3(() => {
    if (activeStep === null) {
      previousPathRef.current = currentPath;
      previousActiveStepRef.current = null;
      pendingTourNavigationRef.current = false;
      return;
    }
    const step = steps[activeStep];
    if (!step) {
      previousPathRef.current = currentPath;
      previousActiveStepRef.current = activeStep;
      return;
    }
    if (isOnRoute(step, currentPath)) {
      pendingTourNavigationRef.current = false;
    }
    const steppedBack = didTourStepBack(previousActiveStepRef.current, activeStep);
    if (tryAdvanceInteractiveStep(activeStep)) {
      previousPathRef.current = currentPath;
      previousActiveStepRef.current = activeStep;
      return;
    }
    if (steppedBack && !isOnRoute(step, currentPath)) {
      if (step.interactionMode === "interactive") {
        if (tryAdvanceInteractiveStep(activeStep, { forceIfReached: true })) {
          previousPathRef.current = currentPath;
          previousActiveStepRef.current = activeStep;
          return;
        }
      }
      navigateForTour(step);
      previousPathRef.current = currentPath;
      previousActiveStepRef.current = activeStep;
      return;
    }
    const nextStep = steps[activeStep + 1];
    if (shouldAdvanceToNextStepRoute(currentPath, step, nextStep)) {
      onTourEvent?.({ type: "route_advance", fromStepIndex: activeStep });
      advanceToNextStep(activeStep);
      previousPathRef.current = currentPath;
      previousActiveStepRef.current = activeStep;
      return;
    }
    if (shouldEndTourOnExternalNavigation({
      activeStep,
      currentPath,
      pendingTourNavigation: pendingTourNavigationRef.current,
      previousActiveStep: previousActiveStepRef.current,
      previousPath: previousPathRef.current,
      step
    })) {
      pendingTourNavigationRef.current = false;
      onTourEvent?.({ type: "external_navigation", stepIndex: activeStep });
      endTour();
      previousPathRef.current = currentPath;
      previousActiveStepRef.current = activeStep;
      return;
    }
    previousPathRef.current = currentPath;
    if (!isOnRoute(step, currentPath)) {
      if (step.interactionMode === "interactive") {
        if (tryAdvanceInteractiveStep(activeStep, { forceIfReached: true })) {
          previousPathRef.current = currentPath;
          previousActiveStepRef.current = activeStep;
          return;
        }
        navigateForTour(step);
        previousActiveStepRef.current = activeStep;
        return;
      }
      navigateForTour(step);
    }
    previousActiveStepRef.current = activeStep;
  }, [
    activeStep,
    advanceToNextStep,
    currentPath,
    endTour,
    launchId,
    navigate,
    onTourEvent,
    pendingTourNavigationRef,
    previousActiveStepRef,
    previousPathRef,
    steps,
    tryAdvanceInteractiveStep
  ]);
};

// src/tour/hooks/useTourStepAdvance.ts
import { useCallback as useCallback3 } from "react";
var useTourStepAdvance = ({ onStepTargetMissing, refs }) => {
  const {
    activeStepRef,
    currentPathRef,
    goToStepRef,
    interactiveAdvancePollRef,
    previousPathRef,
    stepsRef,
    stopTourRef
  } = refs;
  const clearInteractiveAdvancePoll = useCallback3(() => {
    if (interactiveAdvancePollRef.current !== void 0) {
      window.clearInterval(interactiveAdvancePollRef.current);
      interactiveAdvancePollRef.current = void 0;
    }
  }, [interactiveAdvancePollRef]);
  const endTour = useCallback3(() => {
    clearInteractiveAdvancePoll();
    stopTourRef.current();
  }, [clearInteractiveAdvancePoll, stopTourRef]);
  const advanceToNextStep = useCallback3(
    (stepIndex) => {
      clearInteractiveAdvancePoll();
      if (stepIndex + 1 >= stepsRef.current.length) {
        endTour();
        return;
      }
      goToStepRef.current(stepIndex + 1);
    },
    [clearInteractiveAdvancePoll, endTour, goToStepRef, stepsRef]
  );
  const tryAdvanceInteractiveStep = useCallback3(
    (stepIndex, options) => {
      const step = stepsRef.current[stepIndex];
      if (!step || stepIndex + 1 >= stepsRef.current.length) {
        return false;
      }
      if (!canAdvanceInteractiveStep(
        currentPathRef.current,
        previousPathRef.current,
        step,
        options
      )) {
        return false;
      }
      advanceToNextStep(stepIndex);
      return true;
    },
    [advanceToNextStep, currentPathRef, previousPathRef, stepsRef]
  );
  const startInteractiveAdvancePoll = useCallback3(
    (stepIndex) => {
      clearInteractiveAdvancePoll();
      const startedAt = Date.now();
      interactiveAdvancePollRef.current = window.setInterval(() => {
        if (activeStepRef.current !== stepIndex) {
          clearInteractiveAdvancePoll();
          return;
        }
        if (tryAdvanceInteractiveStep(stepIndex, { forceIfReached: true })) {
          clearInteractiveAdvancePoll();
          return;
        }
        if (Date.now() - startedAt > INTERACTIVE_ADVANCE_POLL_TIMEOUT_MS) {
          clearInteractiveAdvancePoll();
          const step = stepsRef.current[stepIndex];
          if (step) {
            onStepTargetMissing?.(stepIndex, step.element);
          }
          endTour();
        }
      }, INTERACTIVE_ADVANCE_POLL_MS);
    },
    [
      activeStepRef,
      clearInteractiveAdvancePoll,
      endTour,
      interactiveAdvancePollRef,
      onStepTargetMissing,
      stepsRef,
      tryAdvanceInteractiveStep
    ]
  );
  return {
    advanceToNextStep,
    clearInteractiveAdvancePoll,
    endTour,
    startInteractiveAdvancePoll,
    tryAdvanceInteractiveStep
  };
};

// src/tour/components/AppTourController.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var AppTourController = ({
  activeStep,
  config,
  currentPath,
  goToStep,
  launchId,
  navigate,
  onStepTargetMissing,
  onTourEvent,
  steps,
  stopTour
}) => {
  const { coreTourConfig, tourConfig } = useNormalizedTourConfig(config);
  const refs = useTourControllerRefs({ activeStep, currentPath, goToStep, steps, stopTour });
  const advance = useTourStepAdvance({ onStepTargetMissing, refs });
  useTourRouteNavigation({
    activeStep,
    advance,
    currentPath,
    launchId,
    navigate,
    onTourEvent,
    refs,
    steps
  });
  useEffect4(() => {
    return () => {
      advance.clearInteractiveAdvancePoll();
    };
  }, [advance.clearInteractiveAdvancePoll, launchId]);
  const currentStep = activeStep !== null ? steps[activeStep] : void 0;
  const isRunning = activeStep !== null && currentStep != null && isOnRoute(currentStep, currentPath);
  const { joyrideSteps, updateLargeTargetPlacement } = useJoyrideStepsWithPlacement({
    activeStep,
    coreTourConfig,
    currentPath,
    isRunning,
    launchId,
    refs,
    steps
  });
  useTourBodyClass(isRunning);
  useAdvanceOnClickListener({
    activeStep,
    advance,
    isRunning,
    launchId,
    refs,
    steps
  });
  const handleEvent = useJoyrideTourEvents({
    advance,
    allowKeyboardControl: tourConfig.allowKeyboardControl ?? true,
    onStepTargetMissing,
    onTourEvent,
    refs,
    updateLargeTargetPlacement
  });
  if (!joyrideSteps.length) {
    return null;
  }
  return /* @__PURE__ */ jsx2(
    AppTourJoyride,
    {
      activeStep: activeStep ?? 0,
      isRunning,
      joyrideSteps,
      onEvent: handleEvent,
      tourConfig
    }
  );
};

// src/tour/store/createTourStore.ts
import { create } from "zustand";
var createTourStore = () => create((set) => ({
  activeStep: null,
  launchContext: {},
  launchId: 0,
  startTour: (context) => set((state) => ({
    activeStep: 0,
    launchContext: context ?? {},
    launchId: state.launchId + 1
  })),
  goToStep: (step) => set({ activeStep: step }),
  stopTour: () => set({
    activeStep: null,
    launchContext: {}
  })
}));
export {
  AppTourController,
  canAdvanceInteractiveStep,
  createTourStore,
  didTourStepBack,
  getNavigationRoute,
  hasReachedAdvancePath,
  isOnRoute,
  isTargetTooLargeForEdgePlacement,
  mapJoyridePlacement,
  queryTourTargetElement,
  shouldAdvanceToNextStepRoute,
  shouldEndTourOnExternalNavigation,
  toJoyrideSteps
};
